import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { fromCents, toCents } from '@mitter-group/catalog';
import { StoreCurrencyService } from 'src/app/services/store-currency.service';
import { trackById } from 'src/app/core/track-by';
import type { IFoodOption, IFoodOptionGroup } from '@mitter-group/contracts';

/**
 * Vista previa de un producto, como lo ve el cliente en la vitrina (ítem 6.3 del plan del
 * 31/10/2026): fotos cuadradas, nombre, descripción, precio y opciones. La abre el editor en una
 * hoja con `breakpoints`; los datos llegan por `componentProps`, que Ionic asigna a estas
 * propiedades.
 *
 * Las fotos (la principal y las demás, plan del 01/11/2026) van en un carrusel que se desliza con
 * el dedo (`scroll-snap`, sin librerías), con un punto por foto. El precio, con la moneda del
 * comercio (`StoreCurrencyService`).
 *
 * Los tamaños (el grupo que define el precio, `priceGroupId`) muestran el precio completo
 * («Grande $ 8,00»); los demás, el adicional («+ $ 1,00»). Sin «desde»: sería una tercera
 * regla de precio, y espera a que la de la vitrina suba a `catalog`.
 */
@Component({
  selector: 'app-preview',
  templateUrl: './preview.page.html',
  styleUrls: ['./preview.page.scss'],
  standalone: false
})
export class PreviewPage {
  readonly trackById = trackById;
  productName = '';
  desc = '';
  /** Todas las fotos, la principal primero. Vacío: no se dibuja ninguna (como la vitrina). */
  photos: string[] = [];
  /** La foto que se ve en el carrusel. */
  photoIndex = 0;
  /** El precio base, como se guardaría. */
  price = NaN;
  /** Los grupos tal como se guardarían: precios como adicional. */
  groups: IFoodOptionGroup[] = [];
  /** El grupo que define el precio (tamaños), si hay. */
  priceGroupId: string | null = null;

  constructor(
    private modalController: ModalController,
    private currency: StoreCurrencyService
  ) {}

  /** Cuál foto quedó a la vista después de deslizar: la que ocupa el carrusel. */
  onPhotosScroll(event: Event) {
    const track = event.target as HTMLElement;
    const width = track.clientWidth;
    if (!width) {
      return;
    }
    const index = Math.round(track.scrollLeft / width);
    this.photoIndex = Math.min(Math.max(index, 0), Math.max(this.photos.length - 1, 0));
  }

  /** El precio de arriba: con tamaños no hay uno solo, lo dicen las opciones. */
  get priceText(): string {
    return this.priceGroupId || !Number.isFinite(this.price) ? '' : this.money(this.price);
  }

  /** Elige uno: radios dentro de un `ion-radio-group`. */
  isSingle(group: IFoodOptionGroup): boolean {
    return group.max === 1;
  }

  optionPrice(group: IFoodOptionGroup, option: IFoodOption): string {
    if (!Number.isFinite(option.price)) {
      return '';
    }
    if (group.id === this.priceGroupId) {
      const total = fromCents(toCents(this.price) + toCents(option.price));
      return Number.isFinite(total) ? this.money(total) : '';
    }
    return option.price > 0 ? `+ ${this.money(option.price)}` : '';
  }

  private money(amount: number): string {
    return this.currency.format(amount);
  }

  closeIt() {
    void this.modalController.dismiss();
  }
}
